/**
 * Save-file intake. A save arrives as parsed JSON (an imported file or a value
 * read back from IndexedDB), so it is `unknown` until this module has checked
 * its envelope. Pure: no DOM, no storage.
 *
 * What is checked: the format tag, the schema version, the presence and kind
 * (object, array or finite number) of every top-level section. What is not:
 * the fields inside each fleet, leg or planner entry. The engine builds its
 * restored state in full before it commits, so a malformed entry fails the
 * hydrate and leaves the running game untouched.
 */
import { SAVE_GAME_FORMAT, SAVE_GAME_VERSION } from '../models/contracts';
import type { SaveGameState } from '../models/contracts';

const TOP_LEVEL_OBJECTS: readonly string[] = [
  'world',
  'counters',
  'ledger',
  'fuelPrice',
  'fleetAcquisition',
  'planner',
];

const TOP_LEVEL_ARRAYS: readonly string[] = [
  'fuelHedges',
  'infrastructure',
  'fleet',
  'legs',
  'maintenance',
  'startedMaintenanceIds',
  'dayStats',
  'history',
  'macroEvents',
  'competitors',
  'competitorRotations',
  'pricingUpdates',
];

const TOP_LEVEL_NUMBERS: readonly string[] = ['savedAtHour'];

function fail(message: string): never {
  throw new Error(`Invalid save file: ${message}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Checks a parsed JSON value against the save-game envelope and returns it
 * typed as `SaveGameState`. Throws an Error naming the first problem found.
 */
export function parseSaveGame(input: unknown): SaveGameState {
  if (!isRecord(input)) fail('expected a JSON object');
  if (input['format'] !== SAVE_GAME_FORMAT) fail(`format must be "${SAVE_GAME_FORMAT}"`);
  if (input['version'] !== SAVE_GAME_VERSION) {
    fail(`version ${String(input['version'])} is not supported (this build reads version ${SAVE_GAME_VERSION})`);
  }
  for (const key of TOP_LEVEL_NUMBERS) {
    const value = input[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) fail(`${key} must be a finite number`);
  }
  if (typeof input['planDirty'] !== 'boolean') fail('planDirty must be a boolean');
  for (const key of TOP_LEVEL_OBJECTS) {
    if (!isRecord(input[key])) fail(`${key} must be an object`);
  }
  for (const key of TOP_LEVEL_ARRAYS) {
    if (!Array.isArray(input[key])) fail(`${key} must be an array`);
  }
  // The sections above are checked for kind only; the cast is what makes them
  // SaveGameState. Entry-level checks happen when the engine rebuilds the world.
  return input as unknown as SaveGameState;
}
